import { Lightning } from "@lightningjs/sdk";
import posthog from "posthog-js";
import { ChannelItemModel, ChannelModel } from "../../lib/models";
import { LiveVideoPlayer } from "./components/LiveVideoPlayer/LiveVideoPlayer";
import { ChannelListBase, LiveTvGroup } from "./components/ChannelListBase/ChannelListBase";
import { PAGES } from "../../lib/utils";
import configs from "../../lib/configs";

export abstract class LiveChannelsBase extends Lightning.Component<any, any> {
    _showingList = true;
    _overlayTimeout: number | null = null;

    protected _currentChannel: ChannelItemModel | null = null;
    protected _currentChannelStartTime: number | null = null;

    protected abstract get _videoPlayer(): LiveVideoPlayer;
    protected abstract get _channelList(): ChannelListBase;
    protected abstract _showOverlay(channel: ChannelItemModel): void;
    protected abstract _hideOverlay(): void;
    protected abstract _onChannelPreviewPrev(): void;
    protected abstract _onChannelPreviewNext(): void;

    override _getFocused(): Lightning.Component | null | undefined {
        return this._showingList ? this._channelList : this._videoPlayer;
    }

    override _firstActive() {
        this.application.emit("setActivePage", PAGES.live.name);
    }

    override _active() {
        if (window.selectedChannel) {
            const foundChannelItem = this._channelList.items.find(
                (item: any) => !item.isGroupTitle && item.item?.name === window.selectedChannel?.name
            );

            if (foundChannelItem) {
                this._playChannel(foundChannelItem.item);
                this._channelList.index = this._channelList.items.findIndex(
                    (item: any) => !item.isGroupTitle && item.item?.name === window.selectedChannel?.name
                );
            } else {
                const first = this._getFirstChannelItem();
                if (first) this._playChannel(first.item);
            }
        } else if (this._channelList.items.length > 0) {
            const first = this._getFirstChannelItem();
            if (first) this._playChannel(first.item);
        }

        if (this._isPreviewEnabled && this._showingList) {
            this._playPreviewVideo();
        }
    }

    override _inactive() {
        this._trackChannelWatchTime();
        this._currentChannel = null;
        this._currentChannelStartTime = null;
        this.stage.gc();
    }

    override _handleBack() {
        if (!this._showingList) {
            this._showList();
            return true;
        }
        return false;
    }

    $liveChannelSelected(channel: ChannelItemModel) {
        this.application.emit("hideMenu");
        this._hideList();
        this._playChannel(channel);
    }

    protected _triggerOverlay(channel: ChannelItemModel) {
        this._showOverlay(channel);
        this._startOverlayTimeout();
    }

    protected _startOverlayTimeout() {
        if (this._overlayTimeout) clearTimeout(this._overlayTimeout);
        this._overlayTimeout = setTimeout(() => this._hideOverlay(), 5000) as unknown as number;
    }

    protected _cancelOverlayTimeout() {
        if (this._overlayTimeout) {
            clearTimeout(this._overlayTimeout);
            this._overlayTimeout = null;
        }
    }

    protected get _isPreviewEnabled(): boolean {
        return configs.livetv?.preview?.enabled === true;
    }

    protected _playPreviewVideo(): void {
        const url = configs.livetv?.preview?.url;
        if (url) {
            this._videoPlayer.playUrl(url);
        }
    }

    protected _showList(): void {
        if (this._showingList) return;
        this._hideOverlay();
        this._showingList = true;
        this._refocus();
        if (this._isPreviewEnabled) {
            this._playPreviewVideo();
        }
    }

    protected _hideList(): void {
        this._showingList = false;
    }

    protected _onChannelPrev() {
        const channel = this._channelList.navigatePrev();
        if (channel) {
            this._playChannel(channel);
            this._triggerOverlay(channel);
        }
    }

    protected _onChannelNext() {
        const channel = this._channelList.navigateNext();
        if (channel) {
            this._playChannel(channel);
            this._triggerOverlay(channel);
        }
    }

    protected _trackChannelWatchTime() {
        if (this._currentChannel && this._currentChannelStartTime) {
            const watchTimeInSeconds = Math.floor((Date.now() - this._currentChannelStartTime) / 1000);
            posthog.capture("ChannelWatchTime", {
                channelId: this._currentChannel.id,
                channelName: this._currentChannel.name,
                watchTimeSeconds: watchTimeInSeconds
            });
        }
    }

    protected _getFirstChannelItem(): any | null {
        return this._channelList.items.find((item: any) => !item.isGroupTitle && item.item) || null;
    }

    protected _playChannel(channel: ChannelItemModel) {
        if (this._isPreviewEnabled && this._showingList) {
            this._currentChannel = channel;
            return;
        }
        if (!this._videoPlayer.isChannelPlaying(channel)) {
            this._trackChannelWatchTime();
            this._currentChannel = channel;
            this._currentChannelStartTime = Date.now();
            this._videoPlayer.channel = channel;
        }
    }

    set data(initialChannels: ChannelModel[]) {
        console.log("Initial channels received:", initialChannels.length);
        this._channelList.addItems(initialChannels);
    }

    set groups(liveTvGroups: LiveTvGroup[]) {
        console.log("Groups received:", liveTvGroups.length);
        this._channelList.addGroups(liveTvGroups);
    }
}
